import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LinkSimple, Trash } from "@phosphor-icons/react";
import { COMMERCIAL_TYPES } from "@/components/units/CommercialUnitFields";
import { UNIT_KINDS } from "@/lib/domivus";

export const RULE_LABEL = { together: "nur gemeinsam", optional: "gemeinsam oder einzeln" };

export const unitTypeText = (u) =>
  (Number(u?.unit_kind) === 2 && COMMERCIAL_TYPES[u?.unit_type]) || UNIT_KINDS[u?.unit_kind] || "—";

// Kompakte Anzeige in Listen: „verbunden mit 001-02-01 (nur gemeinsam)“
export function UnitLinkBadges({ unit, units = [] }) {
  const links = unit?.linked_units || [];
  if (!links.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={`unit-links-${unit.id}`}>
      {links.map((l) => {
        const o = units.find((x) => x.id === l.unit_id);
        return (
          <span key={l.unit_id} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${l.rule === "together" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-sky-200 bg-sky-50 text-sky-800"}`}>
            <LinkSimple size={10} /> {o?.unit_number || "Einheit"} · {RULE_LABEL[l.rule]}
          </span>
        );
      })}
    </div>
  );
}

export function UnitLinksPanel({ unit }) {
  const [links, setLinks] = useState([]);
  const [units, setUnits] = useState([]);
  const [other, setOther] = useState("");
  const [rule, setRule] = useState("together");
  const load = () => api.get(`/units/${unit.id}/links`).then(({ data }) => setLinks(data || [])).catch(() => setLinks([]));
  useEffect(() => {
    load();
    api.get(`/properties/${unit.property_id}`).then(({ data }) => setUnits((data.units || []).filter((u) => u.id !== unit.id))).catch(() => {});
  }, [unit.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (oid, r) => {
    try { await api.put(`/units/${unit.id}/links/${oid}`, { rule: r }); toast.success("Verbindung gespeichert"); setOther(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };
  const remove = async (oid) => { await api.delete(`/units/${unit.id}/links/${oid}`); toast.success("Verbindung entfernt"); load(); };
  const free = units.filter((u) => !links.some((l) => l.unit_id === u.id));
  return (
    <div className="space-y-3" data-testid="unit-links-panel">
      <p className="text-xs text-slate-500">Verbundene Einheiten werden bei Vermarktung und beim Anlegen eines Mietverhältnisses berücksichtigt. „Nur gemeinsam“ verhindert eine Einzelvermietung.</p>
      {links.length === 0 && <div className="text-sm text-slate-500" data-testid="unit-links-empty">Keine verbundenen Einheiten.</div>}
      {links.map((l) => (
        <div key={l.unit_id} className="flex items-center justify-between gap-2 border rounded px-3 py-2" data-testid={`unit-link-row-${l.unit_id}`}>
          <div className="text-sm"><span className="font-mono">{l.unit?.unit_number}</span> <span className="text-slate-500">· {unitTypeText(l.unit)}{l.unit?.location ? ` · ${l.unit.location}` : ""}</span></div>
          <div className="flex items-center gap-2">
            <select className="h-8 border rounded px-2 text-xs bg-white" value={l.rule} onChange={(e) => save(l.unit_id, e.target.value)} data-testid={`unit-link-rule-${l.unit_id}`}>
              <option value="together">nur gemeinsam vermietbar</option><option value="optional">auch einzeln vermietbar</option>
            </select>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-700" onClick={() => remove(l.unit_id)} data-testid={`unit-link-remove-${l.unit_id}`}><Trash size={14} /></Button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
        <select className="h-9 border rounded px-2 text-sm bg-white min-w-[200px]" value={other} onChange={(e) => setOther(e.target.value)} data-testid="unit-link-other">
          <option value="">Einheit wählen…</option>
          {free.map((u) => <option key={u.id} value={u.id}>{u.unit_number} · {unitTypeText(u)}{u.location ? ` · ${u.location}` : ""}</option>)}
        </select>
        <select className="h-9 border rounded px-2 text-sm bg-white" value={rule} onChange={(e) => setRule(e.target.value)} data-testid="unit-link-new-rule">
          <option value="together">nur gemeinsam vermietbar</option><option value="optional">auch einzeln vermietbar</option>
        </select>
        <Button size="sm" disabled={!other} onClick={() => save(other, rule)} data-testid="unit-link-add"><LinkSimple size={14} className="mr-1" /> Verbinden</Button>
      </div>
    </div>
  );
}
